import firebase from 'firebase/app';
import 'firebase/storage';
import { Injectable } from '@angular/core';
import { ItemFile } from '../interfaces/items/item-file';
import { environment } from 'src/environments/environment';

@Injectable()
export class FirebaseStorage {

	private readonly DEFAULT_STORAGE: string = 'FirebaseStorage';

	private readonly LOCAL_STORAGE_KEY: string = 'currentWorkspace';
	private readonly SCHEMA_STORAGE_KEY: string = 'currentSchemaId';

	private savedBucket: string | null = null;
	private savedWorkspace: string | null = null;
	private savedSchemaId: string | null = null;

	constructor() {
		this.initializeWorkspace();
	}

	/**
	 * Initialize workspace from localStorage
	 */
	private initializeWorkspace(): void {
		this.savedWorkspace = window.localStorage.getItem(this.LOCAL_STORAGE_KEY);
		this.savedSchemaId = window.localStorage.getItem(this.SCHEMA_STORAGE_KEY);
		if (this.savedWorkspace) {
			this.savedBucket = `gs://${environment.firebaseConfig.projectId}-${this.savedWorkspace}`;
		}
	}

	/**
	 * Get storage provider title
	 */
	public static get title(): string {
		return 'FirebaseStorage';
	}

	/**
	 * Upload file to client bucket
	 * @param file file info
	 * @param name file name
	 * @param dir file directory
	 * @param progressCallback upload progress callback
	 * @param url file url
	 * @param options upload options
	 * 
	 * @returns item file info
	 */
	// eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
	public uploadFile(file: File, name: string, dir: string, progressCallback: (arg0: { loaded: number, total: number }) => void, url: string, options: any): Promise<ItemFile> {
		const schemaId: number | null = options?.schemaId || (this.savedSchemaId ? Number(this.savedSchemaId) : null);

		const gcsFilename: string = this.generateFilename(file.name, schemaId);

		const blob: Blob = new Blob([file]);

		return new Promise((resolve, reject) => {
			const uploadTask: firebase.storage.UploadTask = firebase.app().storage(this.savedBucket ?? environment.firebaseConfig.storageBucket).ref(gcsFilename).put(blob);

			uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED, (snapshot: firebase.storage.UploadTaskSnapshot): void => {
				progressCallback({
					loaded: snapshot.bytesTransferred,
					total: snapshot.totalBytes
				});
			}, (error: Error): void => {
				reject(error);
			}, (): void => {
				progressCallback({
					loaded: file.size,
					total: file.size
				});

				resolve({
					Id: gcsFilename,
					isFile: true,
					isNew: true,
					name: file.name,
					size: file.size,
					storage: FirebaseStorage.title,
					type: file.type
				});
			});
		});
	}

	/**
	 * Downloads file handler
	 * @param file file info
	 * @returns item file info
	 */
	public async downloadFile(file: ItemFile): Promise<ItemFile> {
		const copy: ItemFile = { ...file };

		const fbStorage: firebase.storage.Storage = firebase.app().storage(this.savedBucket ?? environment.firebaseConfig.storageBucket);
		const ref: firebase.storage.Reference = fbStorage.ref(file.Id);

		copy.url = await ref.getDownloadURL();
		copy.storage = this.DEFAULT_STORAGE;

		return copy;
	}

	/**
	 * Generates a unique file name with schema-based path structure
	 * @param filename file name
	 * @param schemaId schema identifier
	 * @returns unique file path
	 */
	private generateFilename(filename: string, schemaId?: number | null): string {
		const timestamp: number = Date.now();
		const uniqueFilename: string = `${timestamp}_${filename}`;
		
		if (schemaId) {
			return `${schemaId}/files/${uniqueFilename}`;
		}

		return this.savedBucket ? uniqueFilename : `${this.savedWorkspace}_${uniqueFilename}`;
	}

}
